import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { addUser,  getAllUsers, login, updateUserActiveStatus, updateUserMfaStatus, updateUserPhoneNum } from './authenticationAPI';
import { toast } from 'react-toastify';
import { userInterface } from '../../app/consts';
import { jwtDecode } from 'jwt-decode'

export const checkInitialLogin = (): boolean => {
  const token = sessionStorage.getItem('loginToken');
  if (token) {
    try {
      const exp = jwtDecode(token).exp as number;
      if (exp * 1000 > Date.now()) {
        return true;
      }
      logout("auto")
    } catch (error) {
      console.error("Invalid token format");
      return false;
    }
  }
  return false;
};

export interface LoginState {
  isLoggedIn: boolean,
  loginModalShow: boolean,
  addUserModalShow: boolean,
  updatePhoneNumModalShow: boolean,
  isLoading: boolean;
  errorMsg?: string;
  timesCalledLogout: number;
  usersList: Array<userInterface>;
  updateFlag: boolean;
  isOTPCompleted: boolean;
  isReadyForOTP: boolean;
}

const initialState: LoginState = {
  isLoggedIn:checkInitialLogin(),
  loginModalShow: false,
  addUserModalShow: false,
  updatePhoneNumModalShow: false,
  isLoading: false,
  timesCalledLogout: 0,
  usersList: [],
  updateFlag: false,
  isOTPCompleted: false,
  isReadyForOTP: false,
};


export const LoginAsync = createAsyncThunk(
  'authentication/Login',
  async (user: { 'username': string, 'password': string }) => {
    const res = await login(user);
    return res;
  }
);

export const addUserAsync = createAsyncThunk(
  'authentication/addUser',
  async (data: userInterface, { dispatch, rejectWithValue }) => {
    try {
      const res = await addUser(data);
      return res;
    }
    catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(logout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAllUsersAsync = createAsyncThunk(
  'authentication/getAllUsers',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await getAllUsers();
      return res;
    }
    catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(logout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateUserActiveStatusAsync = createAsyncThunk(
  'authentication/updateUserActiveStatus',
  async (username: string, { dispatch, rejectWithValue }) => {
    try {
      const res = await updateUserActiveStatus(username);
      return res;
    }
    catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(logout("auto"));
      }
      return rejectWithValue(error?.response?.data);

    }
  }
);

export const updateUserPhoneNumAsync = createAsyncThunk(
  'authentication/updateUserPhoneNum',
  async (data: { username: string, phone_num: string }, { dispatch, rejectWithValue }) => {
    try {
      const res = await updateUserPhoneNum(data);
      return res;
    }
    catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(logout("auto"));
      }
      return rejectWithValue(error?.response?.data);

    }
  }
);

export const updateUserMfaStatusAsync = createAsyncThunk(
  'authentication/updateUserMfaStatus',
  async (username: string, { dispatch, rejectWithValue, getState }) => {
    try {
      const res = await updateUserMfaStatus(username);
      return res;
    }
    catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(logout("auto"));
      }
      return rejectWithValue(error?.response?.data);

    }
  }
);

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    logout: (state, action) => {
      sessionStorage.removeItem('loginToken');
      state.isLoggedIn = false;
      state.isOTPCompleted = false;
      state.isReadyForOTP = false;
      state.timesCalledLogout === 0 && toast.warning("נותקת מהמערכת, אנא התחבר שנית");
      state.timesCalledLogout += 1;
      if (action.payload === "auto")
        state.loginModalShow = true;
    },
    setLoginModalShow: (state, action: { payload: boolean, type: string }) => {
      state.loginModalShow = action.payload;
      if (action.payload === false && state.errorMsg !== undefined)
        state.errorMsg = undefined;
    },
    setAddUserModalShow: (state, action: { payload: boolean, type: string }) => {
      state.addUserModalShow = action.payload;
      if (action.payload === false && state.errorMsg !== undefined)
        state.errorMsg = undefined;
    },
    setPhoneNumberUpdateModalShow: (state, action: { payload: boolean, type: string }) => {
      state.updatePhoneNumModalShow = action.payload;
      if (action.payload === false && state.errorMsg !== undefined)
        state.errorMsg = undefined;
    },
    setUpdateFlag: (state) => {
      state.updateFlag = !state.updateFlag
    },
    setErrorMessage: (state, action) => {
      state.errorMsg = action.payload
    },
    OTPCompleted: (state) => {
      state.isOTPCompleted = true;
      state.loginModalShow = false;
      state.timesCalledLogout = 0;
      if (window.location.pathname !== "/") {
        window.location.reload();
      }
    },
    setIsReadyForOTP: (state, action) => {
      state.isReadyForOTP = action.payload
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(LoginAsync.pending, (state, action) => {
        state.isLoggedIn = false;
        state.isLoading = true;
        state.errorMsg = "";
      })
      .addCase(LoginAsync.fulfilled, (state, action) => {
        sessionStorage.setItem('loginToken', action.payload.access)
        state.isLoggedIn = true;
        state.isLoading = false;
      })
      .addCase(LoginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        action.error.message?.includes('401') ? state.errorMsg = "שם משתמש או סיסמא שגויים, נסה שנית" : state.errorMsg = "";
      })
      .addCase(getAllUsersAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAllUsersAsync.fulfilled, (state, action) => {
        state.usersList = action.payload;
        state.isLoading = false;
      })
      .addCase(getAllUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addUserAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(addUserAsync.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addUserAsync.rejected, (state, action) => {
        state.isLoading = false;
      })
      ;
  },
});

export const { logout, setErrorMessage, setLoginModalShow, setAddUserModalShow, setUpdateFlag, setPhoneNumberUpdateModalShow, OTPCompleted, setIsReadyForOTP } = authenticationSlice.actions;

export const selectLoginModalShow = (state: RootState) => state.Login.loginModalShow;
export const selectAddUserModalShow = (state: RootState) => state.Login.addUserModalShow;
export const selectUpdatePhoneNumModalShow = (state: RootState) => state.Login.updatePhoneNumModalShow;
export const SelectIsLoading = (state: RootState) => state.Login.isLoading;
export const SelectErrorMessage = (state: RootState) => state.Login.errorMsg;
export const SelectUsersList = (state: RootState) => state.Login.usersList;
export const SelectUpdateFlag = (state: RootState) => state.Login.updateFlag;
export const SelectIsReadyForOTP = (state: RootState) => state.Login.isReadyForOTP;
export const SelectIsLogged = (state:RootState) => state.Login.isLoggedIn

export default authenticationSlice.reducer;
